<template>
  <div>
    <version-diff-modal :onRevertSuccess="handleOnRevertSuccess" />

    <page-heading :title="$t(`${translationPrefix}.title`)"></page-heading>

    <el-card v-if="!policy && !currentUser.admin">
      <div align="center">
        <div class="icon">
          <span class="fa-stack fa-5x">
            <i class="fas fa-building fa-stack-1x"></i>
            <i class="far fa-book-alt fa-stack-2x"></i>
          </span>
        </div>
        <b>There is no company policy yet.</b> Please check back soon!
      </div>
    </el-card>

    <el-row :gutter="20">

      <el-col :span="16">

        <div v-if="(currentUser.admin && edit) || (currentUser.admin && !policy)">
          <content-placeholder v-if="loading">
            <el-card>
              <p class="text text-75"></p>
              <p class="text text-full"></p>
              <p class="text text-50"></p>
              <p class="text text-50"></p>
              <p class="text text-50"></p>
            </el-card>
          </content-placeholder>
          <div v-else>
            <policy-form :policy="policy" :onSuccess="handleOnPolicySaveSuccess">
              <span slot="buttons">
                <el-button @click="edit = false">Cancel</el-button>
              </span>
            </policy-form>
          </div>
        </div>

        <el-card  v-if="!edit && policy" class="officialPolicy">
          <div v-html="policy.text"></div>
        </el-card>

        <content-placeholder v-if="loading">
          <el-card>
            <p class="text text-10 text-h-20"></p>
            <p class="text text-50"></p>
            <p class="text text-25"></p>
            <p class="text text-25"></p>
          </el-card>
        </content-placeholder>
        <div v-else>
          <comments-list v-if="policy" :id="policy.id" type="Policy" :maxHeight="400" />
        </div>

      </el-col>

      <el-col :span="8">

        <div v-if="!policy && currentUser.admin">
          <el-card>
            <el-alert
              title="You do not have a company policy yet."
              type="error"
              center
              :closable="false"
              show-icon>
            </el-alert>
          </el-card>
        </div>

        <div v-if="policy">
          <content-placeholder v-if="loading">
            <el-card>
              <p class="text text-10 text-h-20"></p>
              <p class="text text-50"></p>
              <p class="text text-25"></p>
              <p class="text text-25"></p>
            </el-card>
          </content-placeholder>
          <div v-else>
            <el-card v-if="!currentUser.admin && !currentUser.policy_accepted_at">
              <div slot="header">
                <i class="global-icon fas fa-exclamation-triangle"></i>
                <span>Action Required</span>
              </div>
              <el-alert
                title="Please read the company policy thoroughly and accept changes before continuing."
                type="error"
                center
                :closable="false"
                show-icon>
              </el-alert>
              <div slot="footer">
                <el-button @click="handleAccept" type="danger" class="full-width">Accept</el-button>
              </div>
            </el-card>
          </div>

          <content-placeholder v-if="loading">
            <el-card>
              <p class="text text-10 text-h-20"></p>
              <p class="text text-50"></p>
              <p class="text text-25"></p>
              <p class="text text-25"></p>
            </el-card>
          </content-placeholder>
          <div v-else>
            <el-card>
              <div slot="header">
                <i class="global-icon fas fa-file-alt"></i>
                <span>Policy Information</span>
              </div>
              <span slot="actions">
                <el-tooltip class="item" effect="dark" content="Edit Policy" placement="top">
                  <el-button @click="edit = true" v-if="currentUser.admin">
                    <i class="fas fa-pencil"></i>
                  </el-button>
                </el-tooltip>
                <el-tooltip effect="dark" content="Delete policy. This is NOT recommended." placement="bottom">
                  <el-button type="danger" @click="handleDestroyRevision(policy.id)" v-if="currentUser.admin">
                    <i class="fa fa-trash-alt"/>
                  </el-button>
                </el-tooltip>
              </span>
              <div class="labeled-text">
                <p class="label">Created on:</p>
                <p class="text">{{ policy.created_at | formatTime }}</p>
              </div>
              <div class="labeled-text">
                <p class="label">Updated on:</p>
                <p class="text">{{ policy.updated_at | formatTime }}</p>
              </div>
            </el-card>
          </div>
        </div>

        <content-placeholder v-if="loading">
          <el-card>
            <p class="text text-10 text-h-20"></p>
            <p class="text text-50"></p>
            <p class="text text-25"></p>
            <p class="text text-25"></p>
          </el-card>
        </content-placeholder>
        <div v-else>
          <versions-list ref="versionsList" :id="policy.id" type="Policy" v-if="currentUser.admin && policy" />
        </div>

      </el-col>
    </el-row>

  </div>
</template>

<script>
  import { mapActions, mapGetters } from 'vuex';

  import { showSuccessMessage, showErrorMessage } from '@/ui/message';
  import PolicyForm from '@/components/policy/PolicyForm';
  import CommentsList from '@/components/comment/CommentsList';
  import VersionsList from '@/components/version/VersionsList';
  import VersionDiffModal from '@/components/version/VersionDiffModal';

  export default {
    name: 'PolicyPage',
    components: {
      policyForm: PolicyForm,
      commentsList: CommentsList,
      versionsList: VersionsList,
      versionDiffModal: VersionDiffModal,
    },
    data() {
      return {
        translationPrefix: 'pages.policy',
        edit: false,
        loading: true,
      };
    },
    created() {
      this.fetchPolicy()
        .then(() => {
          this.loading = false;
        });
    },
    computed: {
      ...mapGetters('entities/users', ['authenticated', 'currentUser']),
      ...mapGetters('entities/policies', ['fetching', 'accepting']),

      policy() {
        return this.$store.getters['entities/policies/query']().first();
      },
    },
    methods: {
      ...mapActions('entities/policies', ['create', 'fetchPolicy', 'acceptPolicy', 'deletePolicy']),

      handleAccept() {
        const self = this;
        this.$confirm('You are about to accept policy. Do you want to continue?', 'Warning', {
          confirmButtonText: 'OK',
          cancelButtonText: 'Cancel',
          title: 'Confirm',
        }).then(() => {
          self.acceptPolicy({ data: { } })
            .then((response) => {
              if (response.status) {
                self.$store.dispatch('entities/users/update', { ...response.data.entity });
                showSuccessMessage('Policy has been accepted.');
                self.$router.replace({ name: 'dashboard' });
              } else {
                showErrorMessage(response.error);
              }
            });
        });
      },

      handleDestroyRevision(id) {
        const self = this;
        this.$confirm('Are you sure you want to delete this policy? This is NOT recommended.', {
          confirmButtonText: 'OK',
          cancelButtonText: 'Cancel',
          title: 'Confirm',
        }).then(() => {
          self.deletePolicy(id)
            .then((response) => {
              if (response.status) {
                showSuccessMessage('Policy deleted successfully');
              } else {
                showErrorMessage(response.error);
              }
            });
        });
      },

      handleOnRevertSuccess(response) {
        this.create({ data: response.data.entity.model });
      },

      handleOnPolicySaveSuccess() {
        this.edit = false;
        this.$refs.versionsList.fetchVersionsForObject();
      },
    },
  };
</script>
<style lang="scss">
  @import '../assets/scss/variables';
  .actions{
    .el-button{
      margin-left: 10px;
    }
  }
  p{
    margin-top: 0;
  }
  .icon{
    font-size: 24px;
    padding: 50px 0px;
     i { 
      vertical-align: top;
      &.fa-building{
        color: $--saffron-red-delight;
        line-height: 1.6em;
      }
      &.fa-book-alt{
      }
     }
  }
  .officialPolicy{
    word-break: break-all;
    overflow:hidden;
    background-color: #fffeec;
    box-shadow: 0px 0px 10px rgba(0,0,0,0.1);
    min-height: 400px;
    img{
      max-width: 100%;
    }
  }
</style>



// WEBPACK FOOTER //
// src/pages/Policy.vue